import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthCallback } from './components/AuthCallback';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminUsers } from './pages/AdminUsers';
import { AdminProducts } from './pages/AdminProducts';
import { AdminCoupons } from './pages/AdminCoupons';
import { AdminManageUsers } from './pages/AdminManageUsers';
import { AdminHomeEditor } from './pages/AdminHomeEditor';
import { AdminMetrics } from './pages/AdminMetrics';
import { StoreThemeEditor } from './pages/StoreThemeEditor';
import { SellerCompleteProfile } from './pages/SellerCompleteProfile';
import { SellerDashboard } from './pages/SellerDashboard';
import { SellerDashboardNew } from './pages/SellerDashboardNew';
import { SellerProducts } from './pages/SellerProducts';
import { SellerCategories } from './pages/SellerCategories';
import { WhatsAppConfig } from './pages/WhatsAppConfig';
import { FinancialControl } from './pages/FinancialControl';
import { SalesPageBuilder } from './pages/SalesPageBuilder';
import { PublicSalesPage } from './pages/PublicSalesPage';
import { AdminCourses } from './pages/AdminCourses';
import { SellerCourses } from './pages/SellerCourses';
import { SubscriptionPlans } from './pages/SubscriptionPlans';
import { SubscriptionSuccess } from './pages/SubscriptionSuccess';
import { SubscriptionFailure } from './pages/SubscriptionFailure';
import { SubscriptionPending } from './pages/SubscriptionPending';
import { SellerMercadoPagoConfig } from './pages/SellerMercadoPagoConfig';
import { SellerOrders } from './pages/SellerOrders';
import { SellerCoupons } from './pages/SellerCoupons';
import { MyOrders } from './pages/MyOrders';
import { OrderSuccess } from './pages/OrderSuccess';
import { Settings } from './pages/Settings';
import { StoreAccess } from './pages/StoreAccess';
import { StorePage } from './pages/StorePage';
import { Checkout } from './pages/Checkout';
import { InstallPWA } from './components/InstallPWA';
import { Toaster } from './components/ui/sonner';
import '@/App.css';

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<AuthCallback />} />
      <Route path="/access" element={<StoreAccess />} />
      <Route path="/loja/:storeId" element={<StorePage />} />
      <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/coupons"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminCoupons />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cursos"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminCourses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/manage-users"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminManageUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/home-editor"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminHomeEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/metrics"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminMetrics />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/seller/dashboard"
        element={
          <ProtectedRoute allowedRoles={['seller', 'admin']}>
            <SellerDashboardNew />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/products"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/categories"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/vitrine"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <StoreThemeEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/complete-profile"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerCompleteProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/whatsapp"
        element={
          <ProtectedRoute allowedRoles={['seller', 'admin']}>
            <WhatsAppConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/financeiro"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <FinancialControl />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/paginas"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SalesPageBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/cursos"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerCourses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/planos"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SubscriptionPlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/assinatura/sucesso"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SubscriptionSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/assinatura/erro"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SubscriptionFailure />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/assinatura/pendente"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SubscriptionPending />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/mercadopago"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerMercadoPagoConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/pedidos"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/cupons"
        element={
          <ProtectedRoute allowedRoles={['seller']}>
            <SellerCoupons />
          </ProtectedRoute>
        }
      />
      <Route path="/p/:slug" element={<PublicSalesPage />} />
      <Route path="/pedido/:orderId/sucesso" element={<OrderSuccess />} />
      <Route path="/pedido/:orderId/erro" element={<OrderSuccess />} />
      <Route path="/pedido/:orderId/pendente" element={<OrderSuccess />} />
      <Route path="/orders/my" element={<MyOrders />} />
      
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <InstallPWA />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;